import React, { useState } from 'react';
import { Button, FormLabel } from '@mui/material';
import notify from 'lib/notify';
import { sendApiRequest } from 'lib/api/public';
import Modal from 'components/common/Modal';
import TimezoneSelector from 'components/common/TimezoneSelector';
import { PasswordField } from 'components/common/PasswordField';
import { StrongPasswordIndicator } from 'components/common/StrongPasswordIndicator';

type Props = {
  onSuccess: () => void;
};

const ResetPasswordForm = ({ onSuccess }: Props) => {
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [saving, setSaving] = useState(false);

  const passwordMismatch = Boolean(passwordConfirm) && password !== passwordConfirm;

  const onSave = async () => {
    if (!password || !passwordConfirm) {
      notify('Please enter and confirm your password');
      return;
    }

    if (password.length < 16) {
      notify('Password must be at least 16 characters, with one number, one uppercase and one lowercase letter.');
      return;
    }

    if (password !== passwordConfirm) {
      notify('Password and password confirmation do not match');
      return;
    }

    setSaving(true);

    try {
      await sendApiRequest({
        path: `/users/reset-temporary-password`,
        method: 'POST',
        data: {
          password,
          passwordConfirm,
          timezone,
        },
      });

      onSuccess();

      notify('Password reset');

      setSaving(false);

      window.location.reload();
    } catch (error) {
      setSaving(false);
      notify(error);
    }
  };

  const renderContent = () => (
    <>
      <FormLabel sx={{ color: theme => theme.palette.text.primary, fontWeight: 'bold' }} required>
        New password
      </FormLabel>
      <PasswordField
        fullWidth
        value={password}
        onChange={e => setPassword(e.target.value)}
        size="small"
        testId="@reset-temporary-password"
        autoComplete="new-password"
      />
      <StrongPasswordIndicator password={password} variant="caption" />

      <br />
      <br />

      <FormLabel sx={{ color: theme => theme.palette.text.primary, fontWeight: 'bold' }} required>
        Password confirmation
      </FormLabel>
      <PasswordField
        fullWidth
        value={passwordConfirm}
        onChange={e => setPasswordConfirm(e.target.value)}
        size="small"
        testId="@reset-temporary-password-confirm"
        autoComplete="new-password"
        error={passwordMismatch}
        helperText={passwordMismatch ? 'Must match the password above.' : undefined}
      />

      <br />
      <br />

      <FormLabel sx={{ color: theme => theme.palette.text.primary, fontWeight: 'bold' }} required>
        Timezone
      </FormLabel>
      <TimezoneSelector timezone={timezone} onChange={setTimezone} />
    </>
  );

  const renderActions = () => (
    <Button variant="contained" color="primary" onClick={onSave} disabled={saving}>
      {saving ? 'Saving...' : 'Save'}
    </Button>
  );

  return (
    <Modal
      open={true}
      hideCloseButton
      title="Reset password"
      renderContent={renderContent}
      renderActions={renderActions}
    />
  );
};

export default ResetPasswordForm;
